import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Paperclip, X } from 'lucide-react';
import { SENZA_RISPOSTA, testoValutazione } from '../../utils/formCampi';
import { s } from './FormLista';
import { BadgePagamento, STATI_PAGAMENTO } from './FormStrumenti';

// Tutte le risposte di un form in una tabella stile Excel: una riga per risposta, una colonna per domanda,
// filtro su ogni colonna (tendina con i conteggi per le domande a scelta, "contiene" per le altre) e ordinamento.
const A_SCELTA = ['SCELTA_SINGOLA', 'SCELTA_MULTIPLA', 'ELENCO', 'SI_NO', 'VALUTAZIONE', 'CONSENSO', 'DOCUMENTO', 'FIRMA'];

export const testoRisposta = (c, v) => {
  if (v === undefined || v === null || v === '') return '';
  if (v === true) return c.tipo === 'DOCUMENTO' ? 'Accettato' : 'Sì';
  if (c.tipo === 'SI_NO') return v === 'SI' ? 'Sì' : v === 'NO' ? 'No' : String(v);
  if (c.tipo === 'VALUTAZIONE') return testoValutazione(v, c.stile);
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
};
const norm = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Colonne: domande attuali + quelle tolte nel frattempo ma presenti in qualche risposta (come l'Excel)
export const colonneRisposte = (f, risposte) => {
  const col = new Map();
  for (const c of f.campi || []) if (!SENZA_RISPOSTA.includes(c.tipo)) col.set(c.id, c);
  for (const r of risposte) for (const c of r.campi_snapshot || []) if (!SENZA_RISPOSTA.includes(c.tipo) && !col.has(c.id)) col.set(c.id, c);
  return [...col.values()];
};

// pagamenti: dati dello strumento Pagamento (null = spento) · onPagamento(risposta): apre la finestra del pagamento
export default function RisposteTabella({ f, risposte, cerca, onApri, pagamenti, onPagamento }) {
  const pag = pagamenti?.attivo ? pagamenti.righe : null;
  const colonne = useMemo(() => colonneRisposte(f, risposte), [f, risposte]);
  const [filtri, setFiltri] = useState({});  // { colonna: valore } — scelta esatta o testo "contiene"
  const [ordine, setOrdine] = useState({ col: '_data', giu: true });
  const [sopra, setSopra] = useState(null);  // riga sotto il mouse: la colonna DATA fissa ha bisogno di uno sfondo pieno

  // valori già pronti come testo, una volta sola
  const righe = useMemo(() => risposte.map(r => {
    const t = { _data: r.created_at, _codice: r.codice || '', _pag: pag?.[r.id] ? String(pag[r.id].residuo) : '' };
    for (const c of colonne) {
      if (c.tipo === 'ALLEGATO') { const n = r.allegati.filter(a => a.campo_id === c.id); t[c.id] = n.map(a => a.nome_originale).join(', '); }
      else t[c.id] = testoRisposta(c, r.dati?.[c.id]);
    }
    return { r, t };
  }), [risposte, colonne, pag]);

  // valori possibili (con quante volte) per le colonne a scelta: la multipla conta ogni opzione
  const valori = useMemo(() => {
    const m = {};
    for (const c of colonne) {
      if (!A_SCELTA.includes(c.tipo)) continue;
      const conta = new Map();
      for (const { r } of righe) {
        const v = r.dati?.[c.id];
        const lista = c.tipo === 'SCELTA_MULTIPLA' && Array.isArray(v) ? v : [testoRisposta(c, v)];
        for (const x of lista) conta.set(x || '', (conta.get(x || '') || 0) + 1);
      }
      const ordinate = (c.opzioni || []).filter(o => conta.has(o));
      m[c.id] = [...ordinate, ...[...conta.keys()].filter(k => !ordinate.includes(k)).sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b, 'it')))].map(k => [k, conta.get(k)]);
    }
    return m;
  }, [colonne, righe]);

  const visibili = useMemo(() => {
    const q = norm(cerca.trim());
    let out = righe.filter(({ r, t }) => {
      if (q && !norm(`${t._codice} ${Object.values(t).join(' ')}`).includes(q)) return false;
      for (const [id, val] of Object.entries(filtri)) {
        if (val === undefined) continue;
        if (id === '_codice_t') { if (!norm(t._codice).includes(norm(val))) return false; continue; }
        if (id === '_pag') { const x = pag?.[r.id]; if (!x || (val === 'BANCA' ? !x.proposte.length : x.stato !== val)) return false; continue; }
        const c = colonne.find(x => x.id === id);
        if (!c) continue;
        if (A_SCELTA.includes(c.tipo)) {
          const v = r.dati?.[id];
          if (c.tipo === 'SCELTA_MULTIPLA' && Array.isArray(v) && val !== '') { if (!v.includes(val)) return false; }
          else if (c.tipo === 'SCELTA_MULTIPLA' && val === '') { if (Array.isArray(v) && v.length) return false; }
          else if (testoRisposta(c, v) !== val) return false;
        } else if (val && !norm(t[id]).includes(norm(val))) return false;
      }
      return true;
    });
    const { col, giu } = ordine;
    out = [...out].sort((a, b) => {
      const x = a.t[col] ?? '', y = b.t[col] ?? '';
      const n1 = Number(String(x).replace(',', '.')), n2 = Number(String(y).replace(',', '.'));
      const cmp = col === '_data' ? new Date(x) - new Date(y) : x !== '' && y !== '' && isFinite(n1) && isFinite(n2) ? n1 - n2 : String(x).localeCompare(String(y), 'it', { sensitivity: 'base' });
      return giu ? -cmp : cmp;
    });
    return out;
  }, [righe, filtri, ordine, cerca, colonne, pag]);

  const attivi = Object.keys(filtri).length;
  const setFiltro = (id, v) => setFiltri(p => { const n = { ...p }; if (v === undefined) delete n[id]; else n[id] = v; return n; });
  const ordina = (col) => setOrdine(o => ({ col, giu: o.col === col ? !o.giu : col === '_data' }));
  const freccia = (col) => ordine.col === col ? (ordine.giu ? <ArrowDown size={11} /> : <ArrowUp size={11} />) : null;

  const th = { position: 'sticky', top: 0, background: 'var(--fondo-fisso)', zIndex: 2, textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border2)', fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '.3px', verticalAlign: 'top' };
  const td = { padding: '7px 10px', borderBottom: '1px solid var(--border)', fontSize: 'var(--font-size-sm)', verticalAlign: 'top', maxWidth: '280px', overflowWrap: 'anywhere' };
  // colonna DATA ferma mentre si scorre di lato: sfondo pieno + ombra sul bordo, sennò le altre colonne si vedono sotto
  const fissa = { position: 'sticky', left: 0, boxShadow: '1px 0 0 var(--border2), 4px 0 6px -4px rgba(0,0,0,.18)' };
  const fil = { ...s.inp, padding: '4px 6px', fontSize: 'var(--font-size-xs)', marginTop: '6px', minWidth: '110px', fontWeight: 400 };

  return <>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
      <span style={s.meta}>{visibili.length === righe.length ? `${righe.length} risposte` : `${visibili.length} di ${righe.length} risposte`}</span>
      {attivi > 0 && <button type="button" style={{ ...s.btnSec, padding: '3px 10px' }} onClick={() => setFiltri({})}><X size={12} /> TOGLI FILTRI ({attivi})</button>}
      <span style={{ ...s.meta, marginLeft: 'auto' }}>Clic su un titolo per ordinare · clic su una riga per aprirla</span>
    </div>
    {/* --fondo-fisso: sfondo pieno per intestazione e colonna DATA ferme; scritto così perché lo stile delle schede (index.css, [style*="--bg-card"]) non le trasformi in card */}
    <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid var(--border2)', borderRadius: '12px', background: 'var(--bg-card, var(--bg))', '--fondo-fisso': 'var(--bg-card, var(--bg))' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...th, ...fissa, top: 0, zIndex: 3, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => ordina('_data')}>DATA {freccia('_data')}</th>
            {f.genera_codice && <th style={{ ...th, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => ordina('_codice')}>CODICE {freccia('_codice')}
              <input style={{ ...fil, display: 'block' }} placeholder="contiene…" value={filtri._codice_t || ''} onClick={e => e.stopPropagation()} onChange={e => setFiltro('_codice_t', e.target.value || undefined)} /></th>}
            {pag && <th style={{ ...th, whiteSpace: 'nowrap' }}>
              <div style={{ cursor: 'pointer', display: 'flex', gap: '4px' }} onClick={() => ordina('_pag')} title="Ordina per quanto manca">PAGAMENTO {freccia('_pag')}</div>
              <select style={{ ...fil, display: 'block', width: '100%', ...(filtri._pag !== undefined ? { borderColor: '#F4600C', color: '#F4600C' } : {}) }} value={filtri._pag ?? '__tutti'} onChange={e => setFiltro('_pag', e.target.value === '__tutti' ? undefined : e.target.value)}>
                <option value="__tutti">Tutti</option>
                {Object.entries(STATI_PAGAMENTO).map(([k, v]) => <option key={k} value={k}>{v.l.charAt(0) + v.l.slice(1).toLowerCase()} ({Object.values(pag).filter(x => x.stato === k).length})</option>)}
                {pagamenti.banca && <option value="BANCA">Con bonifico proposto ({Object.values(pag).filter(x => x.proposte.length).length})</option>}
              </select></th>}
            {colonne.map(c => (
              <th key={c.id} style={{ ...th, minWidth: '130px', maxWidth: '260px' }}>
                <div style={{ cursor: 'pointer', display: 'flex', gap: '4px', alignItems: 'flex-start' }} onClick={() => ordina(c.id)} title={c.etichetta}>
                  <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{c.etichetta}</span>{freccia(c.id)}
                </div>
                {A_SCELTA.includes(c.tipo)
                  ? <select style={{ ...fil, display: 'block', width: '100%', ...(filtri[c.id] !== undefined ? { borderColor: '#F4600C', color: '#F4600C' } : {}) }} value={filtri[c.id] === undefined ? '__tutti' : filtri[c.id]} onChange={e => setFiltro(c.id, e.target.value === '__tutti' ? undefined : e.target.value)}>
                      <option value="__tutti">Tutti</option>
                      {(valori[c.id] || []).map(([v, n]) => <option key={v} value={v}>{v === '' ? '(senza risposta)' : v} ({n})</option>)}
                    </select>
                  : <input style={{ ...fil, display: 'block', width: '100%', ...(filtri[c.id] ? { borderColor: '#F4600C' } : {}) }} placeholder="contiene…" value={filtri[c.id] || ''} onChange={e => setFiltro(c.id, e.target.value || undefined)} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibili.map(({ r, t }) => (
            <tr key={r.id} onClick={() => onApri(r)} style={{ cursor: 'pointer', background: sopra === r.id ? 'var(--bg-input)' : undefined }} onMouseEnter={() => setSopra(r.id)} onMouseLeave={() => setSopra(null)}>
              <td style={{ ...td, ...fissa, whiteSpace: 'nowrap', background: sopra === r.id ? 'var(--bg-input)' : 'var(--fondo-fisso)', zIndex: 1 }}>{new Date(r.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}{r.inserita_da && <span title={`Inserita a mano da ${r.inserita_da}`} style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#8A3B00' }}>A MANO</span>}</td>
              {f.genera_codice && <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#F4600C', whiteSpace: 'nowrap' }}>{t._codice}</td>}
              {pag && <td style={{ ...td, whiteSpace: 'nowrap' }} title="Apri il pagamento" onClick={e => { e.stopPropagation(); if (pag[r.id]) onPagamento(r); }}>
                <span style={{ display: 'inline-flex', padding: '3px 6px', margin: '-3px -6px', borderRadius: '8px', border: '1px dashed var(--border2)' }}><BadgePagamento p={pag[r.id]} /></span></td>}
              {colonne.map(c => (
                <td key={c.id} style={td}>
                  {c.tipo === 'ALLEGATO' && t[c.id] ? <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}><Paperclip size={11} />{r.allegati.filter(a => a.campo_id === c.id).length}</span>
                    : t[c.id] || <span style={s.meta}>—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!visibili.length && <div style={{ ...s.vuoto, margin: '16px' }}>Nessuna risposta con questi filtri.</div>}
    </div>
  </>;
}
